// page-backlog-review-committee.ts — R7 : saisie du retour de comité.
//
// C'est cette modale qui transforme une revue en **registre de décisions**.
// Sans elle, on a fait une présentation et rien n'a bougé : la revue suivante
// reproposerait ce que le comité a tranché.
//
// Deux points qu'il faut avoir en tête en lisant ce fichier :
//
// 1. **Le comité se prononce sur la POSITION proposée, pas sur l'idée.** D'où
//    les libellés : sur une proposition de rejet, « Validée » veut dire « vous
//    avez bien fait de l'écarter » et la recommandation RESTE rejetée. L'effet
//    de chaque bouton est donc annoncé en clair sous la ligne — sans quoi la
//    saisie serait un piège.
// 2. **La saisie part en un seul appel.** Un comité tranche une série ; une
//    écriture ligne à ligne laisserait la revue à moitié tranchée au premier
//    rechargement.
//
// La modale ne liste que les recommandations portant le drapeau : on ne saisit
// un retour que sur ce qui a été soumis.

export interface FeedbackReco {
  id: number;
  family: string;
  pile: string;
  to_arbitrate: boolean;
  title: string;
  targets: Array<{ type: string; id: number }>;
}

type Outcome = 'validated' | 'refused' | 'postponed';

interface Draft {
  outcome: Outcome | null;
  comment: string;
  snooze_until: string;
}

const OUTCOMES: Outcome[] = ['validated', 'refused', 'postponed'];

let _drafts = new Map<number, Draft>();

function _codes(reco: FeedbackReco): string {
  return reco.targets
    .filter((tg) => tg.type === 'action')
    .map((tg) => `A-${tg.id}`)
    .join(' + ');
}

/** L'effet réel du retour, pour CETTE position. C'est la phrase qui évite au
 *  PMO de se tromper de bouton sur une proposition de rejet. */
function _effect(pile: string, outcome: Outcome): string {
  if (outcome === 'postponed') return t('review.committee.effect.postponed');
  return t(`review.committee.effect.${pile}.${outcome}`);
}

function _rowHtml(reco: FeedbackReco): string {
  const family = t('review.family.' + reco.family);
  const position = t('review.pile.' + reco.pile);
  const buttons = OUTCOMES.map(
    (o) => `<label style="margin-right:10px;white-space:nowrap">
        <input type="radio" name="outcome-${reco.id}" data-outcome="${o}" data-reco="${reco.id}">
        ${esc(t('review.committee.' + o))}
      </label>`,
  ).join('');
  return `<div class="card" data-feedback="${reco.id}" style="padding:10px;margin-bottom:10px">
      <div style="font-size:var(--fs-sm)">
        <strong>${esc(family)} ${esc(_codes(reco))}</strong> · ${esc(reco.title)}
      </div>
      <div class="text-muted text-xs" style="margin:4px 0">
        ${esc(t('review.committee.proposed', { position }))}
      </div>
      <div style="margin:6px 0">${buttons}</div>
      <div data-effect="${reco.id}" class="text-xs" style="min-height:16px"></div>
      <div data-snooze-wrap="${reco.id}" style="display:none;margin-top:6px">
        <label class="text-xs">${esc(t('review.committee.snoozeUntil'))}
          <input type="date" data-snooze="${reco.id}">
        </label>
      </div>
      <input type="text" data-comment="${reco.id}" style="width:100%;margin-top:6px"
        placeholder="${esc(t('review.committee.commentPlaceholder'))}">
    </div>`;
}

function _bind(recos: FeedbackReco[]): void {
  document.querySelectorAll<HTMLInputElement>('[data-outcome]').forEach((radio) => {
    radio.addEventListener('change', () => {
      const id = Number(radio.dataset.reco);
      const outcome = radio.dataset.outcome as Outcome;
      const reco = recos.find((r) => r.id === id);
      if (!reco) return;
      const draft = _drafts.get(id) || { outcome: null, comment: '', snooze_until: '' };
      _drafts.set(id, { ...draft, outcome });
      const effect = document.querySelector<HTMLElement>(`[data-effect="${id}"]`);
      if (effect) effect.textContent = _effect(reco.pile, outcome);
      const wrap = document.querySelector<HTMLElement>(`[data-snooze-wrap="${id}"]`);
      // Un report sur une recommandation DÉJÀ APPLIQUÉE n'a rien à reporter :
      // l'écriture métier est faite, on ne la défait pas ici.
      if (wrap)
        wrap.style.display = outcome === 'postponed' && reco.pile !== 'applied' ? '' : 'none';
    });
  });
  document.querySelectorAll<HTMLInputElement>('[data-comment]').forEach((input) => {
    input.addEventListener('input', () => {
      const id = Number(input.dataset.comment);
      const draft = _drafts.get(id) || { outcome: null, comment: '', snooze_until: '' };
      _drafts.set(id, { ...draft, comment: input.value });
    });
  });
  document.querySelectorAll<HTMLInputElement>('[data-snooze]').forEach((input) => {
    input.addEventListener('change', () => {
      const id = Number(input.dataset.snooze);
      const draft = _drafts.get(id) || { outcome: null, comment: '', snooze_until: '' };
      _drafts.set(id, { ...draft, snooze_until: input.value });
    });
  });
}

function _payload(recos: FeedbackReco[]): Array<Record<string, unknown>> {
  const items: Array<Record<string, unknown>> = [];
  for (const reco of recos) {
    const draft = _drafts.get(reco.id);
    if (!draft?.outcome) continue;
    const item: Record<string, unknown> = {
      recommendation_id: reco.id,
      outcome: draft.outcome,
      comment: draft.comment,
    };
    if (draft.outcome === 'postponed' && reco.pile !== 'applied') {
      item.snooze_until = draft.snooze_until || null;
    }
    items.push(item);
  }
  return items;
}

/** Ouvre la saisie. `onDone` recharge l'écran de revue — la modale ne rend
 *  jamais elle-même la nouvelle liste. */
export function openCommitteeFeedbackModal(
  reviewId: number,
  recos: FeedbackReco[],
  onDone: () => Promise<void> | void,
): void {
  const flagged = recos.filter((r) => r.to_arbitrate);
  if (!flagged.length) {
    toast(t('review.committee.nothingFlagged'), 'error');
    return;
  }
  _drafts = new Map();
  const today = new Date().toISOString().slice(0, 10);
  const body = `<div>
      <label style="display:block;margin-bottom:10px">${esc(t('review.committee.date'))}
        <input type="date" id="committee-date" value="${today}">
      </label>
      <div class="text-muted text-xs" style="margin-bottom:10px">
        ${esc(t('review.committee.hint'))}
      </div>
      ${flagged.map(_rowHtml).join('')}
    </div>`;
  openWideModal(
    t('review.committee.title'),
    body,
    async () => {
      const items = _payload(flagged);
      if (!items.length) {
        toast(t('review.committee.nothingEntered'), 'error');
        return;
      }
      const missing = items.find((i) => i.outcome === 'postponed' && !i.snooze_until);
      if (missing) {
        toast(t('review.committee.snoozeRequired'), 'error');
        return;
      }
      const date = (document.getElementById('committee-date') as HTMLInputElement | null)?.value;
      if (!date) {
        toast(t('review.committee.dateRequired'), 'error');
        return;
      }
      await api.postCommitteeFeedback(reviewId, { committee_date: date, items });
      closeModal();
      toast(t('review.committee.saved', { n: items.length }));
      await onDone();
    },
    t('review.committee.save'),
  );
  _bind(flagged);
}
